import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import AppRoutes from '@/routes/AppRoutes';
import ProvisioningGate from '@/components/ProvisioningGate';
import { ScopeSessionSync } from '@/components/ScopeSessionSync';
import { Button } from '@/components/ui/button';
import { SilkToaster } from '@workspace/silk-ui';
import { NavigationContextValue, NavigationProvider } from '@/context/NavigationContext';
import { PreferencesProvider, usePreferences } from '@/context/PreferencesContext';
import { SessionProvider, useSession } from '@/context/SessionContext';
import { redirectToLogout } from '@/lib/session';
import { ScopeProvider } from '@workspace/scope-context';
import { TourProvider } from '@workspace/tour';
import MemberInviteLandingPage from '@/views/MemberInviteLandingPage';
import PortalStartPage from '@/views/PortalStartPage';
import PortalLoginPage from '@/views/PortalLoginPage';

export function useBrowserNavigation(): NavigationContextValue {
  const [path, setPath] = useState(() => window.location.pathname + window.location.search);
  const pathRef = useRef(path);

  useEffect(() => {
    const handler = () => {
      const nextPath = window.location.pathname + window.location.search;
      pathRef.current = nextPath;
      setPath(nextPath);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const navigate = useCallback((nextPath: string, options?: { replace?: boolean }) => {
    if (!nextPath || nextPath === pathRef.current) {
      return;
    }
    if (options?.replace) {
      window.history.replaceState({}, '', nextPath);
    } else {
      window.history.pushState({}, '', nextPath);
    }
    pathRef.current = nextPath;
    setPath(nextPath);
  }, []);

  return useMemo(
    () => ({
      path,
      navigate,
    }),
    [navigate, path]
  );
}

function SessionGate({ navigation }: { navigation: NavigationContextValue }) {
  const { session, loading, error, refresh, login } = useSession();
  const logoutRedirecting = useRef(false);
  const normalizedPath = useMemo(() => navigation.path.split('?')[0] || '/', [navigation.path]);
  const signedOut = useMemo(() => {
    const search = navigation.path.includes('?') ? navigation.path.slice(navigation.path.indexOf('?') + 1) : '';
    return new URLSearchParams(search).get('signed_out') === '1';
  }, [navigation.path]);
  const signingOut = normalizedPath === '/signing-out';
  const publicStartRoute = normalizedPath === '/start' || normalizedPath === '/start/';
  const publicMemberInviteRoute = normalizedPath === '/join/member' || normalizedPath === '/join/member/';

  useEffect(() => {
    if (!signingOut || logoutRedirecting.current) {
      return;
    }
    logoutRedirecting.current = true;
    redirectToLogout();
  }, [signingOut]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <p className="text-sm font-medium">Loading portal…</p>
      </div>
    );
  }

  if (signingOut) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="flex w-full max-w-sm flex-col items-center gap-6 rounded-[2.5rem] border border-border/50 bg-card/50 p-12 text-center shadow-sm backdrop-blur-sm">
          <div className="relative flex h-20 w-20 items-center justify-center">
            <div className="absolute inset-0 animate-pulse rounded-full bg-primary/10" aria-hidden />
            <Loader2 className="h-8 w-8 animate-spin text-primary/80" aria-hidden />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">Signing Out</h1>
            <p className="text-sm text-muted-foreground/80 leading-relaxed max-w-[240px]">
              Securing your session and ending your connection to the portal.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    if (signedOut) {
      return <PortalLoginPage signedOut />;
    }
    if (publicStartRoute) {
      return <PortalStartPage path={navigation.path} login={login} />;
    }
    if (publicMemberInviteRoute) {
      return <MemberInviteLandingPage path={navigation.path} login={login} />;
    }
    return <PortalLoginPage />;
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-6 w-6" aria-hidden />
          <p className="text-sm font-medium">{error}</p>
        </div>
        <Button variant="outline" onClick={() => refresh()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <NavigationProvider value={navigation}>
      <ProvisioningGate>
        <AppRoutes path={navigation.path} />
      </ProvisioningGate>
    </NavigationProvider>
  );
}

function PortalToaster() {
  const { toastPosition } = usePreferences();
  return <SilkToaster key={toastPosition} position={toastPosition} />;
}

export default function App() {
  const navigation = useBrowserNavigation();

  return (
    <SessionProvider>
      <ScopeProvider autoSelectFirstTenant={false}>
        <ScopeSessionSync />
        <PreferencesProvider>
          <TourProvider>
            <SessionGate navigation={navigation} />
            <PortalToaster />
          </TourProvider>
        </PreferencesProvider>
      </ScopeProvider>
    </SessionProvider>
  );
}
